import React, { useCallback, useState } from 'react'
import IntroCard from 'components/IntroCard'
import { ContainerStyled } from './styled'
import axios from 'axios'
import { API_POOL_BASE_URL } from 'constants/links'

import { useActiveWeb3React } from 'hooks'
import Sidebar from './Sidebar'
import Rank from './Rank'
import Ref from './Ref'
import Points from './Points'
import EcosystemLoader from './EcosystemLoader'
import ModalSignUp from './ModalSignUp'
import { ecosystemList } from './Points/list'
const EcosystemPoints = () => {
  const { account, library } = useActiveWeb3React()
  const [loading, setLoading] = useState(false)
  const [signLoading, setSignLoading] = useState(false)

  const [inputValue, setInputValue] = useState<string>('')
  const [username, setUsername] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState<string>('')

  const [points, setPoints] = useState<number>(0)
  const [referrals, setReferrals] = useState<any>([])

  const [pointsDetails, setPointsDetails] = useState<any[]>([])

  const [signUpModalIsOpen, setSignUpModalIsOpen] = useState<boolean>(false)

  const [isTestnetUser, setIsTestnetUser] = useState(false)

  const [showContent, setShowContent] = useState(false)
  React.useEffect(() => {
    if (!loading) {
      const timer = setTimeout(() => {
        setShowContent(true)
      }, 100)

      return () => clearTimeout(timer)
    } else {
      setShowContent(false)
      return
    }
  }, [loading])

  // Function to fetch username using axios
  const fetchUsername = useCallback(async () => {
    if (!account) return

    try {
      setLoading(true)
      setSignLoading(false)

      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
      const response = await axios.get(`${API_POOL_BASE_URL}/api/get-id`, {
        params: { address: account, noRanking: 1 },
      })
      const testnetRanking = await axios.get(
        `https://raw.githubusercontent.com/HyperSwapX/HS-PP/refs/heads/main/pp.json`
      )

      setIsTestnetUser(testnetRanking.data.points[account]?.pts > 0)

      if (response.data.success) {
        await new Promise((res) => setTimeout(res, 3000))

        setReferrals(response.data.referrals)
        setUsername(response.data.id)
        setPoints(response.data.pts)
        setSignUpModalIsOpen(false)
        setShowContent(false)
      } else {
        setUsername(null)
      }

      axios
        .get(`${API_POOL_BASE_URL}/api/get-epp`, {
          params: { address: account },
        })
        .then((responsePoints) => {
          const reformatted: Record<string, Record<string, number> & { total: number }> = {}
          for (const [weekKey, weekData] of Object.entries(responsePoints.data)) {
            if (!weekKey.startsWith('week_')) continue

            for (const [partner, points] of Object.entries((weekData as any).partners)) {
              const pointNumber = points as number
              if (pointNumber === 0) continue

              if (!reformatted[partner]) {
                reformatted[partner] = { total: 0 }
              }

              reformatted[partner][weekKey] = pointNumber
              reformatted[partner].total += pointNumber
            }
          }

          let sortedArray = Object.entries(reformatted)
            .sort(([, a], [, b]) => b.total - a.total)
            .map(([partner, data]) => ({ name: partner, ...data }))

          Object.keys(ecosystemList).forEach((partnerId: string) => {
            if (sortedArray.findIndex((a) => a.name === partnerId) === -1) {
              sortedArray = [...sortedArray, { name: partnerId, total: 0 }]
            }
          })

          setPointsDetails(sortedArray)
        })
        .catch((e) => {
          console.warn(e)
        })
    } catch (error) {
      console.error('Failed to fetch username:', error)
      let sortedArray: any[] = []
      Object.keys(ecosystemList).forEach((partnerId: string) => {
        sortedArray = [...sortedArray, { name: partnerId, total: 0 }]
      })
      setPointsDetails(sortedArray)
    } finally {
      setLoading(false)
    }
  }, [account, setLoading])

  // Fetch username on mount
  React.useEffect(() => {
    fetchUsername()
  }, [fetchUsername, account])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const alphanumericRegex = /^[a-zA-Z0-9]*$/

    if (value.length <= 18 && alphanumericRegex.test(value)) {
      setInputValue(value)
    }
  }

  const sign = async () => {
    if (!library || !account) return

    const signer = library.getSigner()
    const timestamp = Math.floor(Date.now() / 1000)
    const message = `Please sign this message to confirm that you are the owner. Timestamp: ${timestamp}`
    setErrorMsg('')
    try {
      const signedMessage = await signer.signMessage(message)
      setSignLoading(true)

      const response = await axios.post(`${API_POOL_BASE_URL}/api/signature`, {
        address: account,
        id: inputValue,
        signature: signedMessage,
        message: message,
      })

      if (!response.data.success) {
        throw new Error('Failed to submit signature')
      }
      setSignUpModalIsOpen(false)
      setSignLoading(false)
      await fetchUsername()
    } catch (error: any) {
      setSignLoading(false)
      setErrorMsg(error?.response?.data?.message ? error?.response?.data?.message : 'An error occurred.')
      console.error('Error signing or sending message:', error)
    }
  }

  const isReferrer = referrals.length > 0

  return (
    <>
      <ModalSignUp
        isOpen={signUpModalIsOpen}
        onClose={() => setSignUpModalIsOpen(false)}
        handleChange={handleChange}
        sign={sign}
        errorMsg={errorMsg}
        inputValue={inputValue}
        loading={signLoading}
      />
      {!showContent && loading === true && signUpModalIsOpen === false && <EcosystemLoader loading={loading} />}
      {showContent && (
        <ContainerStyled>
          <Sidebar username={username} openModal={() => setSignUpModalIsOpen(true)} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            <IntroCard variant={3} Key={'ecosystem'}>
              <h1>Ecosystem Points Program</h1>
              <p>Track your progress and join the HyperSwap Points Program.</p>
            </IntroCard>

            <Rank
              isTestnetUser={isTestnetUser}
              isReferrer={isReferrer}
              points={points}
              username={username}
              openModal={() => setSignUpModalIsOpen(true)}
              pointsDetails={pointsDetails}
            />
            <Points pointsDetails={pointsDetails} points={points} />
            <Ref referrals={referrals} username={username} openModal={() => setSignUpModalIsOpen(true)} />
          </div>
        </ContainerStyled>
      )}
    </>
  )
}

export default EcosystemPoints
